'use client'

import { ClientSelector } from '@/components/client/ClientSelector'
import { Professional } from '@/lib/api/common/interfaces/professional.interface'
import { getCompanies } from '@/lib/api/companies/companies-api'
import {
    COMPANY_TARGET_LABELS,
    createNotification,
    CreateNotificationData,
    NOTIFICATION_TARGET_LABELS,
    NOTIFICATION_TYPE_LABELS,
    NotificationTargetType,
    NotificationType,
} from '@/lib/api/notifications'
import { getProfessionals } from '@/lib/api/professionals/professionals-api'
import { getUsers } from '@/lib/api/users/users-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCampaign,
    MdCheckCircle,
    MdError,
    MdInfo,
    MdNotifications,
    MdSend,
    MdSettings,
    MdWarning,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

interface Company {
    id: number
    name: string
}

interface User {
    id: number
    name: string
    email: string
}

const NOTIFICATION_TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
    INFO: <MdInfo className="text-info" size={20} />,
    SUCCESS: <MdCheckCircle className="text-success" size={20} />,
    WARNING: <MdWarning className="text-warning" size={20} />,
    ERROR: <MdError className="text-error" size={20} />,
    SYSTEM: <MdSettings className="text-secondary" size={20} />,
    ANNOUNCEMENT: <MdCampaign className="text-secondary" size={20} />,
}

export default function NotificationFormPage() {
    const navigate = useNavigate()
    const { isMaster, user } = useAuth()

    const defaultTargetType: NotificationTargetType = isMaster
        ? 'ALL'
        : 'EMPLOYEES'

    const [formData, setFormData] = useState<CreateNotificationData>({
        title: '',
        message: '',
        type: 'INFO',
        targetType: defaultTargetType,
    })

    const [companies, setCompanies] = useState<Company[]>([])
    const [users, setUsers] = useState<User[]>([])
    const [professionals, setProfessionals] = useState<Professional[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)

    useEffect(() => {
        loadData()
    }, [isMaster])

    const loadData = async () => {
        setLoadingData(true)
        try {
            if (isMaster) {
                // Master pode ver empresas e usuarios
                const [companiesRes, usersRes] = await Promise.all([
                    getCompanies(),
                    getUsers({ mediatorStatus: '' }),
                ])

                if (companiesRes.success && Array.isArray(companiesRes.result)) {
                    setCompanies(companiesRes.result)
                }

                if (usersRes.success) {
                    const usersResult = usersRes.result
                    if (Array.isArray(usersResult)) {
                        setUsers(usersResult)
                    } else if (
                        usersResult &&
                        typeof usersResult === 'object' &&
                        'data' in usersResult
                    ) {
                        setUsers((usersResult as { data: User[] }).data)
                    } else {
                        setUsers([])
                    }
                }
            } else {
                // Company ve profissionais e clientes da sua empresa
                // Cliente não é mais carregado aqui: o `ClientSelector` busca sob
                // demanda, paginado. Antes esta tela abria puxando a base inteira.
                const professionalsRes = await getProfessionals()

                if (
                    professionalsRes.success &&
                    Array.isArray(professionalsRes.result)
                ) {
                    setProfessionals(professionalsRes.result)
                }
            }
        } catch (error) {
            console.error('Erro ao carregar dados:', error)
        } finally {
            setLoadingData(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.title.trim()) {
            toast.error('Informe o titulo da notificação')
            return
        }

        if (!formData.message.trim()) {
            toast.error('Informe a mensagem da notificação')
            return
        }

        // Validacoes para master
        if (isMaster) {
            if (
                formData.targetType === 'COMPANY' &&
                !formData.targetCompanyId
            ) {
                toast.error('Selecione a empresa destinatária')
                return
            }

            if (formData.targetType === 'USER' && !formData.targetUserId) {
                toast.error('Selecione o usuario destinatário')
                return
            }

            if (formData.targetType === 'ROLE' && !formData.targetRole) {
                toast.error('Informe o perfil destinatário')
                return
            }
        }

        // Validacoes para company
        if (!isMaster) {
            if (
                formData.targetType === 'PROFESSIONAL' &&
                !formData.targetProfessionalId
            ) {
                toast.error('Selecione o profissional destinatário')
                return
            }

            if (formData.targetType === 'CLIENT' && !formData.targetClientId) {
                toast.error('Selecione o cliente/paciente destinatário')
                return
            }
        }

        setLoading(true)
        try {
            const response = await createNotification(formData)
            if (response.success) {
                toast.success('Notificação enviada com sucesso')
                navigate('/notifications')
            } else {
                toast.error(response.error || 'Erro ao enviar notificação')
            }
        } catch (error) {
            toast.error('Erro ao enviar notificação')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleTargetTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const targetType = e.target.value as NotificationTargetType
        setFormData(prev => ({
            ...prev,
            targetType,
            targetCompanyId: undefined,
            targetUserId: undefined,
            targetProfessionalId: undefined,
            targetClientId: undefined,
            targetRole: undefined,
        }))
    }

    // Determina quais labels usar baseado no tipo de usuario
    const targetLabels = isMaster
        ? NOTIFICATION_TARGET_LABELS
        : COMPANY_TARGET_LABELS

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-circle"
                        onClick={() => navigate('/notifications')}
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdNotifications className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Nova Notificação
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Enviar notificação para{' '}
                            {isMaster ? 'usuários' : 'sua equipe'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Informacoes da Notificacao */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                    <div className="p-4 border-b border-base-300/50">
                        <h2 className="font-semibold text-base-content">
                            Informações da Notificação
                        </h2>
                    </div>
                    <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Título <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Titulo da notificacao"
                                    className="input input-bordered w-full"
                                    maxLength={100}
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Tipo
                                    </span>
                                </label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="select select-bordered w-full"
                                >
                                    {Object.entries(NOTIFICATION_TYPE_LABELS)
                                        .filter(([key]) => {
                                            // SYSTEM e ANNOUNCEMENT somente para master
                                            if (!isMaster && (key === 'SYSTEM' || key === 'ANNOUNCEMENT')) {
                                                return false
                                            }
                                            return true
                                        })
                                        .map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="form-control w-full md:col-span-2">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Mensagem <span className="text-error">*</span>
                                    </span>
                                </label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Conteudo da notificacao..."
                                    className="textarea textarea-bordered w-full min-h-[120px]"
                                    maxLength={1000}
                                    required
                                />
                                <label className="label pt-1">
                                    <span className="label-text-alt text-base-content/50">
                                        {formData.message.length}/1000 caracteres
                                    </span>
                                </label>
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Link (opcional)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    name="link"
                                    value={formData.link || ''}
                                    onChange={handleChange}
                                    placeholder="/admin/pagina ou https://..."
                                    className="input input-bordered w-full"
                                />
                                <label className="label pt-1">
                                    <span className="label-text-alt text-base-content/50">
                                        Link para redirecionar ao clicar
                                    </span>
                                </label>
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Ícone (opcional)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    name="icon"
                                    value={formData.icon || ''}
                                    onChange={handleChange}
                                    placeholder="MdInfo, MdWarning..."
                                    className="input input-bordered w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Destinatarios */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm">
                    <div className="p-4 border-b border-base-300/50">
                        <h2 className="font-semibold text-base-content">
                            Destinatários
                        </h2>
                    </div>
                    <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Enviar para <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    name="targetType"
                                    value={formData.targetType}
                                    onChange={handleTargetTypeChange}
                                    className="select select-bordered w-full"
                                >
                                    {Object.entries(targetLabels).map(
                                        ([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>

                            {/* Opcoes para Master */}
                            {isMaster && formData.targetType === 'COMPANY' && (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Empresa <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <select
                                        name="targetCompanyId"
                                        value={formData.targetCompanyId || ''}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                targetCompanyId: Number(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        className="select select-bordered w-full"
                                        required
                                    >
                                        <option value="">
                                            Selecione uma empresa
                                        </option>
                                        {companies.map(company => (
                                            <option
                                                key={company.id}
                                                value={company.id}
                                            >
                                                {company.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {isMaster && formData.targetType === 'USER' && (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Usuário <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <select
                                        name="targetUserId"
                                        value={formData.targetUserId || ''}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                targetUserId: Number(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        className="select select-bordered w-full"
                                        required
                                    >
                                        <option value="">
                                            Selecione um usuário
                                        </option>
                                        {users.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.name} ({user.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {isMaster && formData.targetType === 'ROLE' && (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Perfil <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <select
                                        name="targetRole"
                                        value={formData.targetRole || ''}
                                        onChange={handleChange}
                                        className="select select-bordered w-full"
                                        required
                                    >
                                        <option value="">
                                            Selecione um perfil
                                        </option>
                                        <option value="company">Empresa</option>
                                        <option value="employee">
                                            Funcionário
                                        </option>
                                        <option value="admin">
                                            Administrador
                                        </option>
                                    </select>
                                </div>
                            )}

                            {/* Opcoes para Company */}
                            {!isMaster &&
                                formData.targetType === 'PROFESSIONAL' && (
                                    <div className="form-control w-full">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">
                                                Profissional{' '}
                                                <span className="text-error">*</span>
                                            </span>
                                        </label>
                                        <select
                                            name="targetProfessionalId"
                                            value={
                                                formData.targetProfessionalId || ''
                                            }
                                            onChange={e =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    targetProfessionalId: Number(
                                                        e.target.value
                                                    ),
                                                }))
                                            }
                                            className="select select-bordered w-full"
                                            required
                                        >
                                            <option value="">
                                                Selecione um profissional
                                            </option>
                                            {professionals.map(professional => (
                                                <option
                                                    key={professional.id}
                                                    value={professional.id}
                                                >
                                                    {professional.name}
                                                    {professional.email &&
                                                        ` (${professional.email})`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                            {!isMaster && formData.targetType === 'CLIENT' && (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Paciente/Cliente{' '}
                                            <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <ClientSelector
                                        value={formData.targetClientId || null}
                                        onChange={clientId =>
                                            setFormData(prev => ({
                                                ...prev,
                                                targetClientId:
                                                    clientId ?? undefined,
                                            }))
                                        }
                                        label=""
                                        placeholder="Buscar paciente/cliente por nome, CPF, e-mail ou WhatsApp..."
                                    />
                                </div>
                            )}
                        </div>

                        {/* Preview da notificacao */}
                        {formData.title && (
                            <div className="mt-6">
                                <label className="label pb-1">
                                    <span className="label-text font-medium text-base-content/70">
                                        Pré-visualização
                                    </span>
                                </label>
                                <div className="bg-base-200/50 rounded-lg p-4 border border-base-300/50">
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0">
                                            {
                                                NOTIFICATION_TYPE_ICONS[
                                                formData.type as NotificationType
                                                ]
                                            }
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-base-content">
                                                {formData.title}
                                            </p>
                                            <p className="text-sm text-base-content/60 mt-1 whitespace-pre-wrap">
                                                {formData.message ||
                                                    'Sua mensagem aqui...'}
                                            </p>
                                            <span className="text-xs text-base-content/50 mt-2 block">
                                                Agora
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Botoes de acao */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/notifications')}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSend size={18} />
                        )}
                        Enviar Notificação
                    </button>
                </div>
            </form>
        </div>
    )
}
